/**
 * DashboardPage — the app's homepage.
 *
 * Composes the canonical workspace chrome (Shell · AppRail · Crown) with
 * dashboard widgets sourced from already-shipped API endpoints:
 *   01 — Overdue plans (warn-tinted; deep-links to /kontakty/$id)
 *   02 — Planned contacts (next 3 days)
 *   03 — Úkoly (personal: mine + delegated; company-wide overdue)
 *   04 — LMS new leads per location (gated on membership.lms_pobocky + user.lms_id)
 *   04b — Termíny ojetých vozidel: STK / servisní prohlídka due within 30 days or
 *         overdue (gated on an ojeta_vozidla read grant; GET /v1/ojeta-vozidla/terminy)
 *   04c — Kontroly vozového parku půjčovny: overdue or upcoming checks (date and / or km limit; gated on a
 *         pujcovna read grant; GET /v1/pujcovna/kontroly/terminy)
 *   05 — Novinky changelog (static)
 *
 * No new endpoints required — reuses `usePlannedContactsQuery` (Phase 4.4) and
 * `useLmsNewLeadsQuery` + `useLmsTakeOverMutation` (Phase 2.7).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Badge } from '@/components/Badge';
import { Pill } from '@/components/Pill';
import { Marker } from '@/components/Marker';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { canLevel } from '@/lib/permissions';
import { usePlannedContactsQuery, type PlannedContact } from '@/modules/kontakty/api';
import { useAcceptTaskMutation, useTasksQuery, type Task } from '@/modules/ukoly/api';
import { formatTaskTermin, isOpenStav, taskTitle } from '@/modules/ukoly/helpers';
import { PrivateMark } from '@/modules/ukoly/PrivateMark';
import { useOjetaTerminyQuery, type OjetoTermin } from '@/modules/ojeta-vozidla/api';
import { mergeTerminy } from '@/modules/ojeta-vozidla/helpers';
import { useActiveFirmaId } from '@/modules/skupiny/otherFirma';
import { useVozidlaTerminyQuery } from '@/modules/vozidla/api';
import { useKontrolyTerminyQuery, type KontrolaTermin } from '@/modules/pujcovna/api';
import { remaining } from '@/modules/pujcovna/kontroly-helpers';
import { TERMIN_LABEL, TERMIN_WINDOW_DAYS, deadlineHint } from '@/modules/ojeta-vozidla/helpers';
import {
  lmsPobockaErrorLabel,
  useLmsNewLeadsQuery,
  useLmsTakeOverMutation,
} from '@/modules/vozidla/api';
import {
  contactDisplayName,
  vehicleBrandLabel,
  vehicleModelLabel,
} from '@/modules/kontakty/helpers';
import { formatDayLong } from '@/modules/kalendar/helpers';
import { NovinkyPanel } from './novinky';
import { useMeQuery } from './api';

/** Local-time YYYY-MM-DD. The DB stores Prague-local dates, so the window
 *  boundaries must be local too — `toISOString()` is UTC and drifts a day near
 *  midnight, which dropped today-dated items out of the "příští 3 dny" window. */
function localIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayIso(): string {
  return localIso(new Date());
}

function tenDaysAgoIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 10);
  return d.toISOString().slice(0, 10);
}

function formatCzDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatWeekday(): string {
  const fmt = new Intl.DateTimeFormat('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long' });
  return fmt.format(new Date());
}

function plannedVehicleLabel(c: PlannedContact): string {
  const brand = vehicleBrandLabel(c);
  const model = vehicleModelLabel(c);
  return [brand, model].filter(Boolean).join(' ') || '—';
}

/** Split a planned-contacts list into overdue (before today) and the next-3-day
 *  window, each sorted oldest-first. Shared by the company + personal cards. */
function windowPlans(
  list: PlannedContact[],
  today: string,
  cutoffNext3: string,
): { overdue: PlannedContact[]; upcoming: PlannedContact[] } {
  const overdue: PlannedContact[] = [];
  const upcoming: PlannedContact[] = [];
  for (const c of list) {
    const d = c.datum_planu;
    if (!d) continue;
    const datum = d.slice(0, 10);
    if (datum < today) overdue.push(c);
    else if (datum <= cutoffNext3) upcoming.push(c);
  }
  // Oldest first; within a day whole-day plans precede timed ones.
  const byWhen = (a: PlannedContact, b: PlannedContact) =>
    (a.datum_planu ?? '').localeCompare(b.datum_planu ?? '') ||
    (a.cas_planu ?? '').localeCompare(b.cas_planu ?? '');
  overdue.sort(byWhen);
  upcoming.sort(byWhen);
  return { overdue, upcoming };
}

/** Active tasks split into overdue (reminder before today) and the next-3-day
 *  window, each oldest-first. Tasks without a reminder date are dropped. */
function windowTasks(
  list: Task[],
  today: string,
  cutoffNext3: string,
): { overdue: Task[]; upcoming: Task[] } {
  const overdue: Task[] = [];
  const upcoming: Task[] = [];
  for (const t of list) {
    if (!isOpenStav(t.stav)) continue;
    const d = t.datum_pripominky;
    if (!d) continue;
    const datum = d.slice(0, 10);
    if (datum < today) overdue.push(t);
    else if (datum <= cutoffNext3) upcoming.push(t);
  }
  overdue.sort((a, b) => (a.datum_pripominky ?? '').localeCompare(b.datum_pripominky ?? ''));
  upcoming.sort((a, b) => (a.datum_pripominky ?? '').localeCompare(b.datum_pripominky ?? ''));
  return { overdue, upcoming };
}

/** Max rows a list card shows by default (the header badge still counts
 *  everything). The paired "příští 3 dny" plans + used-car deadlines cards are
 *  the long lists, so they share a larger cap and stay the same height. */
const CARD_ROW_LIMIT = 8;
const WIDE_CARD_ROW_LIMIT = 20;

export function DashboardPage() {
  const me = useMeQuery();
  const userId = me.data?.user?.id ?? null;
  // Kontakty is a managed module: without a read grant the plan queries would
  // 403, so they never fire and every contact-derived card stays hidden.
  const canKontakty = canLevel(me.data?.membership, 'kontakty', 'read');
  const planned = usePlannedContactsQuery({}, { enabled: canKontakty });
  // Personal slices come from a SERVER-side seller filter, which matches the
  // plan's assignee (`kp.id_prodejce`) plus the manager group-expansion baked
  // into the API. We deliberately do NOT filter the company list client-side
  // on `PlannedContact.id_prodejce` — that field is the *contact* owner, not
  // the *plan* assignee, so a plan assigned to you on a colleague's contact
  // would be missed.
  const mine = usePlannedContactsQuery(userId != null ? { seller: userId } : {}, {
    enabled: userId != null && canKontakty,
  });

  // Window the planned-contacts response into "overdue" (datum_planu < today)
  // and "next 3 days".
  const today = todayIso();
  const cutoffNext3 = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return localIso(d);
  }, []);

  const { overdue, upcoming } = useMemo(
    () => windowPlans(planned.data?.contacts ?? [], today, cutoffNext3),
    [planned.data, today, cutoffNext3],
  );
  const { overdue: myOverdue, upcoming: myUpcoming } = useMemo(
    () => windowPlans(mine.data?.contacts ?? [], today, cutoffNext3),
    [mine.data, today, cutoffNext3],
  );

  // Tasks — "my tasks" (assigned to me) and "tasks I created" (creator = me).
  // Úkoly is a managed module (Stage 7): without a read-level grant the
  // queries never fire and the task cards stay hidden.
  const canUkoly = canLevel(me.data?.membership, 'ukoly', 'read');
  const myTasks = useTasksQuery(userId != null ? { mine: true, stav: 'otevrene' } : { stav: 'otevrene' }, {
    enabled: userId != null && canUkoly,
  });
  const createdTasks = useTasksQuery(
    userId != null ? { creator: userId, stav: 'otevrene' } : { stav: 'otevrene' },
    { enabled: userId != null && canUkoly },
  );
  // Whole-company open tasks — no user filter; the API scopes by id_firmy.
  const companyTasks = useTasksQuery({ stav: 'otevrene' }, { enabled: canUkoly });

  const { overdue: myTaskOverdue, upcoming: myTaskUpcoming } = useMemo(
    () => windowTasks(myTasks.data?.tasks ?? [], today, cutoffNext3),
    [myTasks.data, today, cutoffNext3],
  );
  // Tasks a colleague assigned to me that I haven't accepted yet (stav 'aktivni').
  // Self-created tasks skip the accept step server-side, the id_zadal check is
  // belt and braces. Longest-waiting first, since nobody told the assignee.
  const pendingAccept = useMemo(
    () =>
      (myTasks.data?.tasks ?? [])
        .filter((t) => t.stav === 'aktivni' && t.id_zadal !== userId)
        .sort((a, b) => (a.datum_vytvoreni ?? '').localeCompare(b.datum_vytvoreni ?? '')),
    [myTasks.data, userId],
  );
  const acceptTask = useAcceptTaskMutation();
  async function handleAccept(t: Task) {
    try {
      await acceptTask.mutateAsync(t.id_ukolu);
      toast.ok(`Úkol přijat · ${taskTitle(t)}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Nepodařilo se přijmout úkol');
    }
  }
  // Tasks I delegated to others that are now past their reminder date.
  const createdOverdue = useMemo(() => {
    const list = createdTasks.data?.tasks ?? [];
    return list
      .filter(
        (t) =>
          isOpenStav(t.stav) &&
          t.id_prijemce !== userId &&
          t.datum_pripominky != null &&
          t.datum_pripominky.slice(0, 10) < today,
      )
      .sort((a, b) => (a.datum_pripominky ?? '').localeCompare(b.datum_pripominky ?? ''));
  }, [createdTasks.data, today, userId]);
  // Company-wide overdue tasks — lets anyone spot colleagues' tasks slipping.
  const { overdue: companyTaskOverdue } = useMemo(
    () => windowTasks(companyTasks.data?.tasks ?? [], today, cutoffNext3),
    [companyTasks.data, today, cutoffNext3],
  );

  // Ojetá vozidla deadlines (STK / servisní prohlídka) — company-wide (and group-wide, below), so the
  // card lives in the "Celá firma" block; hidden without a read grant.
  const canOjeta = canLevel(me.data?.membership, 'ojeta_vozidla', 'read');
  // Used-car and registered new-car deadlines, each behind its own grant.
  const canVozidla = canLevel(me.data?.membership, 'vozidla', 'read');
  // A member of several firmas of one group sees the group's cars; the server narrows the scope to the
  // firmas where they hold read on the module (a firma without a group yields just the active one).
  const ojetaTerminy = useOjetaTerminyQuery(TERMIN_WINDOW_DAYS, { enabled: canOjeta, rozsah: 'skupina' });
  const noveTerminy = useVozidlaTerminyQuery(TERMIN_WINDOW_DAYS, { enabled: canVozidla, rozsah: 'skupina' });
  const terminy = {
    data: { terminy: mergeTerminy(ojetaTerminy.data?.terminy, noveTerminy.data?.terminy) },
    isLoading: (canOjeta && ojetaTerminy.isLoading) || (canVozidla && noveTerminy.isLoading),
    // One source failing must not blank the other: only an error from every enabled source is shown.
    error:
      [canOjeta && ojetaTerminy, canVozidla && noveTerminy]
        .filter((q): q is typeof ojetaTerminy => !!q)
        .every((q) => q.error)
        ? (ojetaTerminy.error ?? noveTerminy.error)
        : null,
  };

  // Rental fleet checks (date and / or km limit) — a fleet-wide card behind the module's read grant.
  const canPujcovna = canLevel(me.data?.membership, 'pujcovna', 'read');
  const kontroly = useKontrolyTerminyQuery({ enabled: canPujcovna });

  const user = me.data?.user;
  const membership = me.data?.membership;
  // LMS card requires both the company (an LMS location with signing keys) and
  // the user (uzivatele.lms_id, the prodejce id — the same at every location).
  // Without the latter every "Převzít" call hits a 400 from take-over.
  // Leads are contacts (take-over creates a kontakt), so it also needs kontakty.
  const showLmsCard =
    canKontakty && (membership?.lms_pobocky.length ?? 0) > 0 && Boolean(user?.lms_id);

  return (
    <Shell rail={<AppRail active="prehled" />}>
      <Crown>
        <Breadcrumb segments={[{ label: 'Intranet' }, { label: 'Přehled' }]} />
        <Crown.Row className="mt-3">
          <div>
            <DisplayHeading>
              {user ? `Vítej, ${user.jmeno}.` : 'Vítej.'}{' '}
              <em>dnešní přehled.</em>
            </DisplayHeading>
          </div>
          <CrownMeta
            items={[
              ...(canKontakty
                ? [
                    { label: 'Po termínu', value: overdue.length },
                    { label: 'Příští 3 dny', value: upcoming.length },
                  ]
                : []),
              { label: 'Firma', value: membership?.Nazev ?? '—' },
              { label: 'Datum', value: formatWeekday() },
            ]}
          />
        </Crown.Row>
      </Crown>

      <main
        // Scrollable landmark must be keyboard-reachable (axe
        // scrollable-region-focusable) — the dashboard cards are the only
        // route where the shell's <main> is the scroll container itself.
        tabIndex={0}
        aria-label="Přehled"
        className="flex min-h-0 flex-1 flex-col overflow-auto bg-bg focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
      >
        {/* grid-cols-1 (= minmax(0,1fr)) — a bare implicit track floors at the
            widest card's min-content, and a PlanCard row's truncate spans
            contribute their full nowrap text width, so one long client name
            forced ~400px and horizontally scrolled the phone viewport. */}
        <div className="grid grid-cols-1 gap-6 px-8 py-8 max-sm:gap-4 max-sm:px-4 max-sm:py-5 sm:grid-cols-2">
          {user && (canKontakty || canUkoly) && (
            <>
              <SectionLabel tone="warn">— Jen pro vás</SectionLabel>
              {canKontakty && (
              <>
              <PlanCard
                testid="dashboard-overdue-mine"
                personal
                tone="warn"
                title={
                  <>
                    Plánované kontakty,{' '}
                    <em className="font-light italic text-ink-3">po termínu.</em>
                  </>
                }
                data={myOverdue}
                loading={mine.isLoading}
                error={mine.error}
                emptyTitle="Vše vyřízeno."
                emptyDescription="Nemáte žádné plány po termínu."
              />
              <PlanCard
                testid="dashboard-planned-mine"
                personal
                tone="accent"
                title={
                  <>
                    Plánované kontakty,{' '}
                    <em className="font-light italic text-ink-3">příští 3 dny.</em>
                  </>
                }
                data={myUpcoming}
                loading={mine.isLoading}
                error={mine.error}
                emptyTitle="Volné dny."
                emptyDescription="V příštích 3 dnech nemáte plány."
              />
              </>
              )}
              {/* Úkoly get their own 3-up row (the contact cards stay 2-up);
                  the whole row disappears without a read-level ukoly grant. */}
              {canUkoly && (
              <>
              {/* Assigned to me, not yet accepted: the assignee gets no other
                  signal, so this is a call to action. Only rendered when
                  something is waiting (no slim "all clear" line). */}
              {pendingAccept.length > 0 && (
                <div className="sm:col-span-2">
                  <TaskCard
                    testid="dashboard-tasks-pending-accept"
                    personal
                    tone="accent"
                    title={
                      <>
                        Úkoly zadané vám,{' '}
                        <em className="font-light italic text-ink-3">čekají na přijetí.</em>
                      </>
                    }
                    data={pendingAccept}
                    loading={false}
                    error={null}
                    emptyTitle=""
                    emptyDescription=""
                    onAccept={handleAccept}
                    accepting={acceptTask.isPending ? acceptTask.variables : null}
                  />
                </div>
              )}
              <div className="grid grid-cols-1 gap-6 max-sm:gap-4 sm:col-span-2 lg:grid-cols-3">
                <TaskCard
                  testid="dashboard-tasks-overdue-mine"
                  personal
                  tone="warn"
                  title={
                    <>
                      Úkoly,{' '}
                      <em className="font-light italic text-ink-3">po termínu.</em>
                    </>
                  }
                  data={myTaskOverdue}
                  loading={myTasks.isLoading}
                  error={myTasks.error}
                  emptyTitle="Vše stíháte."
                  emptyDescription="Nemáte žádné úkoly po termínu."
                />
                <TaskCard
                  testid="dashboard-tasks-upcoming-mine"
                  personal
                  tone="accent"
                  title={
                    <>
                      Úkoly,{' '}
                      <em className="font-light italic text-ink-3">příští 3 dny.</em>
                    </>
                  }
                  data={myTaskUpcoming}
                  loading={myTasks.isLoading}
                  error={myTasks.error}
                  emptyTitle="Volné dny."
                  emptyDescription="V příštích 3 dnech nemáte úkoly."
                />
                <TaskCard
                  testid="dashboard-tasks-created-overdue"
                  personal
                  tone="warn"
                  title={
                    <>
                      Úkoly, zadané kolegům,{' '}
                      <em className="font-light italic text-ink-3">po termínu.</em>
                    </>
                  }
                  data={createdOverdue}
                  loading={createdTasks.isLoading}
                  error={createdTasks.error}
                  emptyTitle="Nic nevisí."
                  emptyDescription="Žádný vámi zadaný úkol není po termínu."
                  showAssignee
                />
              </div>
              </>
              )}
              <SectionLabel>— Celá firma</SectionLabel>
            </>
          )}

          {canKontakty && (
          <PlanCard
            testid="dashboard-overdue"
            tone="warn"
            title={
              <>
                Plánované kontakty,{' '}
                <em className="font-light italic text-ink-3">po termínu.</em>
              </>
            }
            data={overdue}
            loading={planned.isLoading}
            error={planned.error}
            emptyTitle="Vše vyřízeno."
            emptyDescription="Žádné plány po termínu."
          />
          )}
          {/* Overdue plans + overdue tasks pair up on the first company row so
              everything "po termínu" reads as one glance; the next-3-days card
              drops to the second row. */}
          {canUkoly && (
            <TaskCard
              testid="dashboard-tasks-overdue"
              tone="warn"
              title={
                <>
                  Úkoly,{' '}
                  <em className="font-light italic text-ink-3">po termínu.</em>
                </>
              }
              data={companyTaskOverdue}
              loading={companyTasks.isLoading}
              error={companyTasks.error}
              emptyTitle="Vše vyřízeno."
              emptyDescription="Žádné úkoly po termínu."
              showAssignee
            />
          )}
          {canKontakty && (
          <PlanCard
            testid="dashboard-planned"
            tone="accent"
            title={
              <>
                Plánované kontakty,{' '}
                <em className="font-light italic text-ink-3">příští 3 dny.</em>
              </>
            }
            data={upcoming}
            loading={planned.isLoading}
            error={planned.error}
            emptyTitle="Volné dny."
            emptyDescription="V příštích 3 dnech nejsou plány."
            limit={WIDE_CARD_ROW_LIMIT}
          />
          )}
          {(canOjeta || canVozidla) && (
            <TerminyCard
              data={terminy.data?.terminy ?? []}
              loading={terminy.isLoading}
              error={terminy.error}
            />
          )}
          {canPujcovna && (
            <KontrolyCard data={kontroly.data?.terminy ?? []} loading={kontroly.isLoading} error={kontroly.error} />
          )}
          {showLmsCard && <LmsLeadsCard />}
          <div className="sm:col-span-2">
            <NovinkyPanel limit={5} />
          </div>
        </div>
      </main>
    </Shell>
  );
}

/** Collapsed presentation for an empty card — a single slim line instead of a
 *  tall bordered panel. People who don't keep plans/úkoly were staring at a wall
 *  of empty boxes; this shrinks each "nothing here" card to one muted row while
 *  keeping the grid cell (and its testid) in place. The full panel returns the
 *  instant any item exists. */
function EmptyCardLine({
  testid,
  title,
  label,
}: {
  testid: string;
  title: ReactNode;
  label: string;
}) {
  return (
    <section
      data-testid={testid}
      data-empty="true"
      // self-start so the grid doesn't stretch this single line to the full row
      // height of a neighbouring full card (the grid defaults to align stretch).
      className="flex items-center justify-between gap-4 self-start border border-line bg-paper px-6 py-3"
    >
      <h2 className="min-w-0 truncate font-display text-[15px] leading-none text-ink-3">
        {title}
      </h2>
      <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
        <Icon name="check" size={12} className="text-ok" />
        {label}
      </span>
    </section>
  );
}

/** Full-width grid label separating the personal cards from the company ones. */
function SectionLabel({ children, tone = 'ink' }: { children: ReactNode; tone?: 'ink' | 'warn' }) {
  return (
    <div className="-mb-2 flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-3 sm:col-span-2">
      <span aria-hidden className={`inline-block h-px w-3.5 ${tone === 'warn' ? 'bg-warn' : 'bg-accent'}`} />
      {children}
    </div>
  );
}

type PlanTone = 'warn' | 'accent';

interface PlanCardProps {
  testid: string;
  title: ReactNode;
  tone: PlanTone;
  data: PlannedContact[];
  loading: boolean;
  error: unknown;
  emptyTitle: string;
  emptyDescription: string;
  /** "Jen pro vás" cards get a tinted shell so they read as personal; an
   *  overdue personal card with items escalates to the strong warn treatment. */
  personal?: boolean;
  /** Cap the rendered rows (the marker still shows the true total). */
  limit?: number;
}

function PlanCard({
  testid,
  title,
  tone,
  data,
  loading,
  error,
  emptyTitle,
  emptyDescription,
  personal = false,
  limit = CARD_ROW_LIMIT,
}: PlanCardProps) {
  // Nothing to show → collapse to a slim line rather than a tall empty panel.
  if (!loading && !error && data.length === 0) {
    return <EmptyCardLine testid={testid} title={title} label={emptyTitle} />;
  }
  const alerting = personal && tone === 'warn' && data.length > 0;
  const shell = alerting
    ? 'border-2 border-red bg-red-soft'
    : personal
      ? 'border border-accent bg-accent-soft'
      : 'border border-line bg-paper';
  const markerTone = tone === 'warn' ? (data.length > 0 ? 'danger' : 'ok') : 'accent';
  const dateClass = tone === 'warn' ? 'text-red-text' : 'text-accent-text';
  const rows = data.slice(0, limit);
  return (
    <section data-testid={testid} className={`${shell} px-6 py-5`}>
      <header className="mb-3 flex items-baseline justify-between border-b border-line pb-3">
        <h2 className="font-display text-[22px] leading-none">{title}</h2>
        <Marker tone={markerTone}>{data.length}</Marker>
      </header>
      {loading ? (
        <div className="space-y-2 py-2">
          <Skeleton height={14} />
          <Skeleton height={14} width="70%" />
          <Skeleton height={14} width="85%" />
        </div>
      ) : error ? (
        <EmptyState eyebrow="Chyba načítání" title="Plány nelze načíst." description="Zkuste obnovit stránku." />
      ) : data.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((p) => (
            <li key={p.id_kontaktu}>
              <Link
                to="/kontakty/$id"
                params={{ id: String(p.id_kontaktu) }}
                className="flex items-baseline justify-between gap-4 py-2.5 hover:bg-accent-soft"
              >
                <span className={`font-mono text-[11px] uppercase tracking-[0.06em] ${dateClass}`}>
                  {formatCzDate(p.datum_planu)}
                  {p.cas_planu ? ` ${p.cas_planu}` : ''}
                </span>
                <span className="flex-1 truncate text-[13px] text-ink">
                  {contactDisplayName(p)}
                </span>
                <span className="hidden truncate text-[11px] text-ink-3 sm:inline">
                  {p.prodejce_jmeno || '—'}
                </span>
                <span className="truncate font-mono text-[11px] text-ink-3">
                  {plannedVehicleLabel(p)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface TaskCardProps {
  testid: string;
  title: ReactNode;
  tone: PlanTone;
  data: Task[];
  loading: boolean;
  error: unknown;
  emptyTitle: string;
  emptyDescription: string;
  personal?: boolean;
  /** Show the assignee instead of "you" — used by the "tasks I created" card. */
  showAssignee?: boolean;
  /** Renders a "Přijmout" button on each row (tasks awaiting the viewer's accept). */
  onAccept?: (t: Task) => void;
  /** id of the task whose accept request is in flight. */
  accepting?: number | null;
}

function TaskCard({
  testid,
  title,
  tone,
  data,
  loading,
  error,
  emptyTitle,
  emptyDescription,
  personal = false,
  showAssignee = false,
  onAccept,
  accepting = null,
}: TaskCardProps) {
  // Nothing to show → collapse to a slim line rather than a tall empty panel.
  if (!loading && !error && data.length === 0) {
    return <EmptyCardLine testid={testid} title={title} label={emptyTitle} />;
  }
  const alerting = personal && tone === 'warn' && data.length > 0;
  const shell = alerting
    ? 'border-2 border-red bg-red-soft'
    : personal
      ? 'border border-accent bg-accent-soft'
      : 'border border-line bg-paper';
  const markerTone = tone === 'warn' ? (data.length > 0 ? 'danger' : 'ok') : 'accent';
  const dateClass = tone === 'warn' ? 'text-red-text' : 'text-accent-text';
  const rows = data.slice(0, CARD_ROW_LIMIT);
  return (
    <section data-testid={testid} className={`${shell} px-6 py-5`}>
      <header className="mb-3 flex items-baseline justify-between border-b border-line pb-3">
        <h2 className="font-display text-[22px] leading-none">{title}</h2>
        <Marker tone={markerTone}>{data.length}</Marker>
      </header>
      {loading ? (
        <div className="space-y-2 py-2">
          <Skeleton height={14} />
          <Skeleton height={14} width="70%" />
          <Skeleton height={14} width="85%" />
        </div>
      ) : error ? (
        <EmptyState eyebrow="Chyba načítání" title="Úkoly nelze načíst." description="Zkuste obnovit stránku." />
      ) : data.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((t) => (
            <li key={t.id_ukolu} className={onAccept ? 'flex items-center gap-3' : undefined}>
              <Link
                to="/ukoly/$id"
                params={{ id: String(t.id_ukolu) }}
                // No list cohort here — show the whole company on the detail rail
                // so opening a colleague's task doesn't land on an empty "Moje" rail.
                search={{ mine: false, stav: 'otevrene' }}
                className="flex min-w-0 flex-1 items-baseline justify-between gap-4 py-2.5 hover:bg-accent-soft"
              >
                <span className={`font-mono text-[11px] uppercase tracking-[0.06em] ${dateClass}`}>
                  {formatTaskTermin(t)}
                </span>
                <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-ink">
                  <PrivateMark task={t} />
                  <span className="truncate">{taskTitle(t)}</span>
                </span>
                <span className="truncate text-[11px] text-ink-3">
                  {showAssignee ? t.prijemce_jmeno || '—' : t.zadal_jmeno || '—'}
                </span>
              </Link>
              {onAccept && (
                <Button
                  size="sm"
                  icon={<Icon name="check" size={12} />}
                  disabled={accepting === t.id_ukolu}
                  onClick={() => onAccept(t)}
                >
                  Přijmout
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function terminVehicleLabel(t: OjetoTermin): string {
  return [t.nazev_znacky, t.nazev_modelu].filter(Boolean).join(' ') || `Vozidlo № ${t.id_vozidla}`;
}

/** Vehicle (used and registered new) deadlines due within the look-ahead window or already overdue —
 *  one row per (vehicle, deadline kind), overdue first. A deadline already
 *  booked in servis (same pairing as the calendar: `naplanovano`) shows the
 *  booked day. Deep-links to the vehicle detail where the date is edited. */
function TerminyCard({
  data,
  loading,
  error,
}: {
  data: OjetoTermin[];
  loading: boolean;
  error: unknown;
}) {
  const title = (
    <>
      Vozidla,{' '}
      <em className="font-light italic text-ink-3">termíny STK · servisní prohlídka.</em>
    </>
  );
  if (!loading && !error && data.length === 0) {
    return <EmptyCardLine testid="dashboard-terminy" title={title} label="Vše platné." />;
  }
  // Overdue but already booked in servis isn't alarming — only unbooked ones are.
  const overdueUnplanned = data.filter((t) => t.dnu < 0 && !t.naplanovano).length;
  const markerTone = overdueUnplanned > 0 ? 'danger' : data.length > 0 ? 'warn' : 'ok';
  const rows = data.slice(0, WIDE_CARD_ROW_LIMIT);
  // Name the firma once any row belongs to a firma other than the active one (otherwise it is noise).
  const activeFirmaId = useActiveFirmaId();
  const showFirma = activeFirmaId != null && data.some((t) => t.id_firmy != null && t.id_firmy !== activeFirmaId);
  return (
    <section data-testid="dashboard-terminy" className="border border-line bg-paper px-6 py-5">
      <header className="mb-3 flex items-baseline justify-between border-b border-line pb-3">
        <h2 className="font-display text-[22px] leading-none">{title}</h2>
        <Marker tone={markerTone}>{data.length}</Marker>
      </header>
      {loading ? (
        <div className="space-y-2 py-2">
          <Skeleton height={14} />
          <Skeleton height={14} width="70%" />
          <Skeleton height={14} width="85%" />
        </div>
      ) : error ? (
        <EmptyState eyebrow="Chyba načítání" title="Termíny nelze načíst." description="Zkuste obnovit stránku." />
      ) : (
        <ul className="@container divide-y divide-line">
          {rows.map((t) => {
            const isOverdue = t.dnu < 0 && !t.naplanovano;
            return (
              <li key={`${t.zdroj ?? 'ojete'}-${t.id_vozidla}-${t.druh}`}>
                <a
                  href={`/${t.zdroj === 'nove' ? 'vozidla' : 'ojeta-vozidla'}/${t.id_vozidla}`}
                  className="block py-2.5 hover:bg-accent-soft"
                >
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 @xl:flex-nowrap">
                    <span
                      className={`w-[6.5rem] shrink-0 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.06em] ${isOverdue ? 'text-red-text' : 'text-warn'}`}
                    >
                      {formatCzDate(t.datum)}
                    </span>
                    <span className="w-[9rem] shrink-0 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-2">
                      {TERMIN_LABEL[t.druh]}
                    </span>
                    <span className="min-w-0 text-[13px] text-ink @max-xl:order-last @max-xl:basis-full @xl:flex-1 @xl:truncate">
                      {terminVehicleLabel(t)}
                    </span>
                    <span className={`shrink-0 whitespace-nowrap text-[11px] @max-xl:ml-auto ${isOverdue ? 'text-red-text' : 'text-ink-3'}`}>
                      {deadlineHint(t.dnu)}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 @xl:ml-[17.5rem]">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                      <Badge size="sm" tone={t.zdroj === 'nove' ? 'accent' : 'neutral'}>
                        {t.zdroj === 'nove' ? 'nové' : 'ojeté'}
                      </Badge>
                      <Pill>{t.spz_vozu || 'bez SPZ'}</Pill>
                      {showFirma && t.firma_nazev && (
                        <Pill className="max-w-full truncate">
                          <span data-testid="dashboard-termin-firma" title={`Vozidlo firmy ${t.firma_nazev}`}>
                            {t.firma_nazev}
                          </span>
                        </Pill>
                      )}
                    </div>
                    {/* Right-aligned, directly under the "po termínu …" hint above. */}
                    {t.naplanovano && (
                      <span
                        data-testid="dashboard-termin-naplanovano"
                        className="ml-auto whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[0.06em] text-ok"
                      >
                        Naplánováno {formatDayLong(t.naplanovano)}
                      </span>
                    )}
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function kontrolaVehicleLabel(k: KontrolaTermin): string {
  return [k.znacka_nazev, k.model_nazev].filter(Boolean).join(' ') || k.spz || k.vin;
}

/** Rental fleet checks that are overdue or upcoming (a date and / or a km limit, whichever comes first) — one row
 *  per check, overdue first. The API judges them; the row only phrases the verdict and links to the fleet, where
 *  the car's "Kontroly" modal completes them. */
function KontrolyCard({ data, loading, error }: { data: KontrolaTermin[]; loading: boolean; error: unknown }) {
  const title = (
    <>
      Půjčovna,{' '}
      <em className="font-light italic text-ink-3">kontroly vozidel.</em>
    </>
  );
  if (!loading && !error && data.length === 0) {
    return <EmptyCardLine testid="dashboard-kontroly" title={title} label="Vše v pořádku." />;
  }
  const overdue = data.filter((k) => k.stav === 'po_terminu').length;
  const markerTone = overdue > 0 ? 'danger' : data.length > 0 ? 'warn' : 'ok';
  const rows = data.slice(0, WIDE_CARD_ROW_LIMIT);
  return (
    <section data-testid="dashboard-kontroly" className="border border-line bg-paper px-6 py-5">
      <header className="mb-3 flex items-baseline justify-between border-b border-line pb-3">
        <h2 className="font-display text-[22px] leading-none">{title}</h2>
        <Marker tone={markerTone}>{data.length}</Marker>
      </header>
      {loading ? (
        <div className="space-y-2 py-2">
          <Skeleton height={14} />
          <Skeleton height={14} width="70%" />
          <Skeleton height={14} width="85%" />
        </div>
      ) : error ? (
        <EmptyState eyebrow="Chyba načítání" title="Kontroly nelze načíst." description="Zkuste obnovit stránku." />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((k) => {
            const isOverdue = k.stav === 'po_terminu';
            return (
              <li
                key={`${k.druh}-${k.id ?? k.id_vozidla}`}
                data-testid={k.druh === 'servis_prohlidka' ? `dashboard-servis-${k.id_vozidla}` : `dashboard-kontrola-${k.id}`}
                data-stav={k.stav}
              >
                <a href="/pujcovna/vozidla" className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5 hover:bg-accent-soft">
                  <span
                    className={`shrink-0 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.06em] ${isOverdue ? 'text-red-text' : 'text-warn'}`}
                  >
                    {k.limit_datum ? formatCzDate(k.limit_datum) : `limit ${k.limit_km?.toLocaleString('cs-CZ')} km`}
                  </span>
                  <span className="shrink-0 truncate text-[13px] text-ink">{k.nazev}</span>
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink-2">{kontrolaVehicleLabel(k)}</span>
                  <span className="hidden truncate font-mono text-[11px] text-ink-3 sm:inline">{k.spz || 'bez SPZ'}</span>
                  <span className={`text-[11px] max-sm:basis-full sm:truncate ${isOverdue ? 'text-red-text' : 'text-ink-3'}`}>
                    {remaining(k).join(' · ')}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function LmsLeadsCard() {
  const [datum, setDatum] = useState<string | null>(null);
  const leads = useLmsNewLeadsQuery(datum);
  const takeOver = useLmsTakeOverMutation();

  function handleFetch() {
    setDatum(tenDaysAgoIso());
  }

  function handleTakeOver(leadId: number, pobockaId: number) {
    takeOver.mutate(
      { leadId, pobockaId },
      {
        onSuccess: (data) => toast.ok(`Lead převzat · ${data.jmeno}`),
        onError: (e) =>
          toast.error(
            `Chyba: ${e instanceof Error ? e.message : 'převzetí leadu se nezdařilo'}`,
          ),
      },
    );
  }

  const pobocky = leads.data?.pobocky ?? [];
  // Location names only matter once a company has more than one.
  const showNames = pobocky.length > 1;
  const nothingToShow = pobocky.every((p) => !p.error && p.new_ids.length === 0);

  return (
    <section
      data-testid="dashboard-lms"
      className="border border-line bg-paper px-6 py-5"
    >
      <header className="mb-3 flex items-baseline justify-between border-b border-line pb-3">
        <h2 className="font-display text-[22px] leading-none">
          LMS, <em className="font-light italic text-ink-3">nové leady.</em>
        </h2>
        <Button variant="secondary" size="sm" mono onClick={handleFetch} disabled={leads.isFetching}>
          {leads.isFetching ? 'Načítám…' : 'Získat nové leady'}
        </Button>
      </header>
      {!datum ? (
        <EmptyState
          title="Vyžádejte si seznam leadů."
          description="Stažení leadů z LMS spustí HMAC-signed dotaz, takže ho posíláme až na vyžádání."
        />
      ) : leads.error ? (
        <EmptyState eyebrow="Chyba LMS" title="LMS odpověděl chybou." description="Zkuste znovu za chvíli." />
      ) : !leads.data ? null : nothingToShow ? (
        <EmptyState title="Žádné nové leady." description="Aktuálně nic k převzetí." />
      ) : (
        <div className="flex flex-col gap-4">
          {pobocky.map((p) => {
            const error = lmsPobockaErrorLabel(p.error);
            // Single location: skip it entirely when it has nothing to say.
            if (!showNames && !error && p.new_ids.length === 0) return null;
            return (
              <div key={p.id} data-testid="dashboard-lms-pobocka">
                {showNames && (
                  <h3 className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                    {p.nazev}
                  </h3>
                )}
                {error ? (
                  <p className="py-2 font-mono text-[11px] text-warn">{error}</p>
                ) : p.new_ids.length === 0 ? (
                  <p className="py-2 font-mono text-[11px] text-ink-3">Žádné nové leady.</p>
                ) : (
                  <ul className="divide-y divide-line">
                    {p.new_ids.map((id) => (
                      <li key={id} className="flex items-center justify-between gap-3 py-2.5">
                        <span className="font-mono text-[12px] text-ink">Lead #{id}</span>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleTakeOver(id, p.id)}
                          disabled={takeOver.isPending}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <Icon name="check" size={12} />
                            Převzít
                          </span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
